import { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { PaginateList } from 'src/app/common/types';
import { getDefaultDisplayDate } from 'src/app/common/utils/time-utils';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import ItemDeleteDialog from 'src/app/common/components/ItemDeleteDialog';
import { MasterDataSettingItem, ProductField } from '../../../../types/master-data-setting-types';
import { deleteMasterDataSettingItem } from '../../../../network/master-data-setting-crud';
import { masterDataSettingPath } from '../../master-data-setting-routes';
import { isPermitted, useJWT } from 'src/app/common/utils';
import { map } from 'lodash';
import { AdminCommissionToolPermissionType, LanguageKey } from 'src/app/modules/CommissionTool/constants';
import { useTranslation } from 'src/app/common/hook/translation.hook';

type MasterDataSettingListProps = {
  isLoading: boolean;
  masterDataSettingList?: PaginateList<MasterDataSettingItem>;
  onRefresh: () => void;
};

type DialogState = {
  open: boolean;
  id: string;
  name: string;
};

export const MasterDataSettingList: FC<MasterDataSettingListProps> = ({
  isLoading,
  masterDataSettingList,
  onRefresh,
}) => {
  const Translation = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  let readPermission = false;
  let updatePermission = false;
  const jwt = useJWT() || {};

  if (jwt.permissions) {
    const jwtPermission = map(jwt.permissions, (_, key) => key);
    readPermission = isPermitted([AdminCommissionToolPermissionType.MASTER_DATA_SETTING_READ], jwtPermission);
    updatePermission = isPermitted([AdminCommissionToolPermissionType.MASTER_DATA_SETTING_UPDATE], jwtPermission);
  }

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    id: '',
    name: '',
  });

  const onDelete = async (id: string) => {
    try {
      await deleteMasterDataSettingItem(id, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: Translation('master.data.setting.update.success', {
              name: Translation(LanguageKey[dialogState.name as ProductField]) || '',
            }),
          },
        ]),
      );
    } catch (err) {}
  };

  return (
    <>
      <ItemDeleteDialog
        key={`delete-master-data-dialog-${dialogState.open}`}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, id: '', name: '' })}
        onRefresh={onRefresh}
        onDelete={() => onDelete(dialogState.id)}
        content={Translation('master.data.setting.delete.confirm')}
      />
      <PruTable
        title={Translation('master.data.setting.list')}
        disableBulkSelect
        disablePagination
        operationDef={
          updatePermission || readPermission
            ? [
                {
                  title: Translation('section.common.operation.view'),
                  tooltipText: 'View',
                  onClick: (row) => history.push(`${masterDataSettingPath}/view/${row._id}`),
                  condition: () => readPermission,
                },
                {
                  title: Translation('section.common.operation.edit'),
                  tooltipText: 'Edit',
                  onClick: (row) => history.push(`${masterDataSettingPath}/edit/${row._id}`),
                  condition: () => updatePermission,
                },
              ]
            : []
        }
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'name',
            displayName: Translation('master.data.setting.name'),
            renderData: (row) => Translation(`master.data.setting.name.${row.name}`),
            style: { whiteSpace: 'nowrap', width: 170 },
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('master.data.setting.createdAt'),
            renderData: (row) => getDefaultDisplayDate(row.createdAt, 'dateTime'),
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('master.data.setting.updatedAt'),
            renderData: (row) => getDefaultDisplayDate(row.updatedAt, 'dateTime'),
          },
          {
            keyIndex: 'updatedBy',
            align: 'center',
            displayName: Translation('master.data.setting.updatedBy'),
            renderData: (row) => row.updatedBy || '',
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={masterDataSettingList?.docs}
      />
    </>
  );
};
